'use client'

import Loader from '@/components/common/loader'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import apiFetch from '@/store/helpers/api-fetch'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import toast from 'react-hot-toast'
import useSWR from 'swr'

export default function CommunityMemberStatusButton({ session, community }) {
  const router = useRouter()
  const {
    data: communityMember,
    isLoading: communityMemberLoading,
    mutate,
  } = useSWR(
    session
      ? {
          url: `community/${community.id}/member/${session.user.id}/role/driver`,
        }
      : null,
    apiFetch,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  )

  async function joinCommunity() {
    toast.loading('Joining community...', { id: 'join-community' })

    try {
      await apiFetch({
        url: `community/${community.id}/member/${session.user.id}/role/driver`,
        method: communityMember ? 'PATCH' : 'POST',
        data: {
          status: 'registered',
        },
      })

      // mixpanel.track('Community Status Changed', {
      //   ...community,
      //   status: 'registered',
      // })

      await mutate()

      router.refresh()

      toast.success('Joined community!', { id: 'join-community' })
    } catch (e) {
      toast.error(e.message, { id: 'join-community' })
    }
  }

  if (communityMemberLoading) {
    return (
      <Skeleton className="fixed bottom-6 z-50 h-[52px] w-[calc(100%-48px)] rounded-sm desktop-xs:relative desktop-xs:bottom-0 desktop-xs:z-auto desktop-xs:block desktop-xs:w-[158px]" />
    )
  }

  if (!communityMember || communityMember.status !== 'registered') {
    return (
      <Button
        type="button"
        className="bg-linear-to-b text-white! hover:from-primary-600! hover:to-primary-600! fixed bottom-6 z-50 h-[52px] w-[calc(100%-48px)] border-none from-primary-500 to-[#FF3D00] desktop-xs:relative desktop-xs:bottom-0 desktop-xs:z-auto desktop-xs:w-[158px]"
        onClick={joinCommunity}
      >
        Join Community
      </Button>
    )
  }

  return (
    <ConfirmWithdrawDialog
      community={community}
      session={session}
      mutate={mutate}
      router={router}
    />
  )
}

function ConfirmWithdrawDialog({ community, session, mutate, router }) {
  const [submitting, setSubmitting] = useState(false)
  const [open, setOpen] = useState(false)

  async function leaveCommunity() {
    setSubmitting(true)

    try {
      await apiFetch({
        url: `community/${community.id}/member/${session.user.id}/role/driver`,
        method: 'PATCH',
        data: {
          status: 'withdrawn',
        },
      })

      // mixpanel.track('Community Status Changed', {
      //   ...community,
      //   status: 'withdrawn',
      // })

      await mutate()

      router.refresh()

      setOpen(false)
      toast.success('Left community!', { id: 'leave-community' })
    } catch (e) {
      toast.error(e.message, { id: 'leave-community' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          className="bg-neutral-blue-900! px-8! text-shades-0! transition-all! hover:bg-neutral-blue-800! desktop-xs:py-4! fixed bottom-6 z-50 h-fit w-[calc(100%-48px)] rounded-sm border border-shades-0 py-[21px] text-p-3 font-semibold ease-in-out desktop-xs:relative desktop-xs:bottom-0 desktop-xs:z-auto desktop-xs:block desktop-xs:w-fit"
        >
          Leave Community
        </Button>
      </DialogTrigger>
      <DialogContent className="min-h-[150px] w-4/5 p-5 sm:w-[400px] md:sm:w-[500px]">
        <DialogHeader>
          <DialogTitle>Confirm Leaving Community</DialogTitle>
          <DialogDescription>
            This will also remove you from all events and championships in this
            community.
          </DialogDescription>
        </DialogHeader>
        {submitting ? (
          <Loader
            content="Leaving community..."
            className="mb-5 h-[80px] text-center"
          />
        ) : (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="min-w-1/2 grow"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="min-w-1/2 grow"
              onClick={leaveCommunity}
            >
              Yes, leave community
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
